import React, { useState } from 'react';
import { MarketplaceSellerProfile } from '../../../slices/profiles-data-slice/models/marketplace-seller-profile';
import { OwnershipTypeOptions } from '../../../slices/marketplace/timeline/models/enums/ownership-type.enum';
import { LocationTypeOptions } from '../../../slices/marketplace/timeline/models/enums/location-type.enum';
import BootstrapTable from '../../react-bootstrap-table-next/react-bootstrap-table2';
import ImageCarousel from '../../ImageCarousel/ImageCarousel';
import './DisclosedProfile.scss';
import { RevenueGrowthRateOptions } from '../../../slices/marketplace/timeline/models/enums/revenue-growth.enum';
import { EntityLocationInfo } from '../../../slices/profiles-data-slice/models/entity-location-info';
import { useServiceFocuses } from '../../../custom-hooks/useServiceFocuses';
import {
    getOtherFocus,
    getServiceFocusById,
    isOtherFocus,
} from '../../../containers/UserPages/Login/Modals/PracticeRegistrationForm/utils';

interface DisclosedProfileProps {
    profileData?: MarketplaceSellerProfile;
}

const DisclosedProfile: React.FC<DisclosedProfileProps> = (props) => {
    const { profileData } = props;
    const serviceFocuses = useServiceFocuses();
    const [activePracticeIndex, setActivePracticeIndex] = useState(0);

    const practices = profileData?.practiceProfiles || [];
    const activePractice = practices[activePracticeIndex];
    const practiceImages =
        activePractice?.uploads
            ?.filter((img) => img && img.source && img.source.trim() !== '')
            .map((img) => img.source) || [];

    const renderTabs = () => {
        if (practices.length <= 1) return null;

        return (
            <div className="RRT__container">
                <div className="RRT__tabs">
                    {practices.map((practice, index) => (
                        <div
                            key={practice.id}
                            className={
                                activePracticeIndex === index
                                    ? 'RRT__tab RRT__tab--first RRT__tab--selected'
                                    : 'RRT__tab'
                            }
                            onClick={() => setActivePracticeIndex(index)}>
                            {practice.practiceName}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderPracticeInfo = () => {
        if (!activePractice) return null;

        return (
            <div className="practice-info-section">
                <div className="section-title">Practice Information</div>
                <div className="info-grid">
                    <div className="info-item">
                        <span className="label">Practice Name</span>
                        <span className="value">
                            {activePractice.practiceName || ''}
                        </span>
                    </div>
                    <div className="info-item">
                        <span className="label">Owner Name</span>
                        <span className="value">
                            {activePractice.ownerFullName || ''}
                        </span>
                    </div>
                    <div className="info-item">
                        <span className="label">Ownership Type</span>
                        <span className="value">
                            {activePractice.practiceOwnershipType
                                ? OwnershipTypeOptions[
                                      activePractice.practiceOwnershipType
                                  ]
                                : ''}
                        </span>
                    </div>
                    <div className="info-item">
                        <span className="label">Service Focus</span>
                        <span className="value">
                            {getServiceFocusLabel(
                                activePractice.practiceFocusType,
                                activePractice.otherPracticeType
                            )}
                        </span>
                    </div>
                    <div className="info-item">
                        <span className="label">Number of FTE DVMs</span>
                        <span className="value">
                            {activePractice.fteDvms || ''}
                        </span>
                    </div>
                    <div className="info-item">
                        <span className="label">Revenue Growth Rate</span>
                        <span className="value">
                            {activePractice.revenueGrowthRate
                                ? RevenueGrowthRateOptions[
                                      activePractice.revenueGrowthRate
                                  ]
                                : ''}
                        </span>
                    </div>
                </div>

                <div className="description-section">
                    <span className="label">Description</span>
                    <span className="value">{activePractice.description}</span>
                </div>
            </div>
        );
    };

    const getLocationsColumns = () => {
        return [
            {
                dataField: 'locationNumber',
                text: 'Number of locations',
                sort: true,
            },
            {
                dataField: 'streetAddress',
                text: 'Street Address',
                sort: true,
            },
            {
                dataField: 'city',
                text: 'City',
                sort: true,
            },
            {
                dataField: 'state',
                text: 'State',
                sort: true,
            },
            {
                dataField: 'zipCode',
                text: 'Zip',
                sort: true,
            },
            {
                dataField: 'locationType',
                text: 'Location Type',
                sort: true,
                formatter: (cellContent: number) =>
                    cellContent
                        ? LocationTypeOptions[
                              cellContent as keyof typeof LocationTypeOptions
                          ]
                        : '',
            },
            {
                dataField: 'dbaName',
                text: 'DBA Name',
                sort: true,
            },
            {
                dataField: 'fteDvms',
                text: 'FTE DVMs',
                sort: true,
            },
            {
                dataField: 'practiceFocusType',
                text: 'Service Focus',
                sort: true,
                formatter: (
                    cellContent: number,
                    row: EntityLocationInfo
                ) => getServiceFocusLabel(cellContent, row.otherPracticeType),
            },
        ];
    };

    const getServiceFocusLabel = (
        practiceFocusType?: number,
        otherPracticeType?: string
    ): string => {
        if (!practiceFocusType) {
            return '';
        }

        const isOther = isOtherFocus(
            serviceFocuses,
            activePractice.practiceFocusType
        );

        if (isOther) {
            return (
                otherPracticeType || getOtherFocus(serviceFocuses)?.name || ''
            );
        }

        return (
            getServiceFocusById(serviceFocuses, practiceFocusType)?.name || ''
        );
    };

    const getLocationsTableData = () => {
        if (!activePractice?.practiceLocations?.length) return [];

        return activePractice.practiceLocations.map((location, index) => ({
            id: location.id,
            locationNumber: `Location ${index + 1}`,
            streetAddress: location.addressInfo?.streetAddress || '',
            city: location.addressInfo?.city || '',
            state: location.addressInfo?.state || '',
            zipCode: location.addressInfo?.zipCode || '',
            locationType: location.locationType,
            dbaName: location.dbaName || '',
            fteDvms: location.fteDvms || '',
            practiceFocusType: location.practiceFocusType,
            otherPracticeType: location.otherPracticeType,
        }));
    };

    const renderLocationsTable = () => {
        if (!activePractice?.practiceLocations?.length) return null;

        return (
            <div className="locations-section">
                <div className="locations-table">
                    <BootstrapTable
                        bootstrap4
                        keyField="id"
                        data={getLocationsTableData()}
                        columns={getLocationsColumns()}
                        noDataIndication="No locations available"
                    />
                </div>
            </div>
        );
    };

    return (
        <div className="disclosed-profile">
            <div className="profile-content">
                {renderTabs()}
                <div className="profile-main-content">
                    <div className="left-section">{renderPracticeInfo()}</div>
                    <div className="right-section">
                        <ImageCarousel
                            images={practiceImages}
                            autoPlayInterval={5000}
                            showArrows={true}
                            showDots={true}
                            maxDots={5}
                            className="practice-carousel"
                        />
                    </div>
                </div>
                {renderLocationsTable()}
            </div>
        </div>
    );
};

export default DisclosedProfile;
